import { initGTM } from './gtm';
import { initSentry } from './sentry';
import { ensureSafeLocalStorage } from './utils/safeLocalStorage';
import { trackCount } from './utils/sentryMetrics';
import { installStaleAssetReloadHandler } from './utils/staleAssetReload';

ensureSafeLocalStorage();
installStaleAssetReloadHandler();
initSentry();
initGTM();
trackCount('portal.web.app.start', 1, {
  environment: import.meta.env.MODE || 'development',
});

import { MotionConfig } from 'framer-motion';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { Toaster } from 'sonner';

import './index.css';
import { AuthInitializer } from './components/AuthInitializer';
import { NetworkIndicator } from './components/dashboard/NetworkIndicator';
import { AlertProvider } from './contexts/AlertContext';
import { AuthStateProvider } from './contexts/AuthState';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { PublisherProvider } from './contexts/PublisherContext';
import { TRPCProvider } from './contexts/TRPC';
import { WalletContextProvider } from './contexts/WalletProvider';
import { router } from './routes';

const disableNetworkIndicator =
  import.meta.env.VITE_DISABLE_NETWORK_INDICATOR === 'true';

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister().then(() => {
        console.log('[ServiceWorker] Unregistered:', registration.scope);
      });
    });
  });
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <TRPCProvider>
    <AuthStateProvider>
      <AlertProvider>
        <PublisherProvider>
          <PreferencesProvider>
            <WalletContextProvider>
              <Toaster
                position="bottom-right"
                richColors
                toastOptions={{
                  style: {
                    background: 'rgb(23 23 23)',
                    color: 'white',
                    border: '1px solid rgb(38 38 38)',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    fontSize: '14px',
                    fontWeight: '400',
                    boxShadow:
                      '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    minHeight: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  },
                }}
              />
              {!disableNetworkIndicator && <NetworkIndicator />}
              <AuthInitializer>
                <MotionConfig reducedMotion="user">
                  <RouterProvider router={router} />
                </MotionConfig>
              </AuthInitializer>
            </WalletContextProvider>
          </PreferencesProvider>
        </PublisherProvider>
      </AlertProvider>
    </AuthStateProvider>
  </TRPCProvider>,
);

if (import.meta.env.DEV) {
  // @ts-expect-error
  window.triggerSentryError = () => {
    throw new Error('[Test] This is a test error for Sentry!');
  };
  console.log('Sentry test error available: window.triggerSentryError()');
}
